import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import { FilterSelect, HpButton, HpCardHeader, HpField, PageHeading, Segmented } from '@hp/ui';
import { ENRICHMENT_PROVIDERS, type EnrichmentMatchStatus, type EnrichmentProviderName } from '@hp/domain';
import { useCan, useMe } from '../api/me';
import { trpc } from '../api/trpc';
import { fmtInt, progressOf } from '../lib/format';
import { isRunActive } from '../lib/runs';
import { ErrorAlert } from '../components/error-alert';
import { Spinner } from '../components/ui/icons';
import { LoadMore } from '../components/ui/load-more';
import { MatchCard } from '../features/enrichment/match-card';
import { PeopleFinder } from '../features/enrichment/people-finder';
import {
  invalidateEnrichment,
  useEnrichmentMatches,
  useEnrichmentRuns,
  useEnrichmentStats,
} from '../features/enrichment/queries';
import { RunHistory } from '../features/enrichment/run-history';
import { StatsStrip } from '../features/enrichment/stats-strip';
import { TABS } from '../features/enrichment/tabs';
import { PublishSection } from '../features/publish/publish-section';

export default function Enrichment() {
  const { t } = useTranslation(['enrichment', 'common']);
  const me = useMe();
  const can = useCan();
  const canSpend = can('credits.spend');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // SPA navigation to /crm-data/enrichment#publish should land on the publish section
  useEffect(() => {
    if (location.hash === '#publish') document.getElementById('publish')?.scrollIntoView();
  }, [location.hash]);
  const rawStatus = searchParams.get('status');
  const status: EnrichmentMatchStatus = TABS.some((tab) => tab.value === rawStatus)
    ? (rawStatus as EnrichmentMatchStatus)
    : 'pending';
  const rawProvider = searchParams.get('provider');
  const providerFilter = ENRICHMENT_PROVIDERS.some((p) => p === rawProvider)
    ? (rawProvider as EnrichmentProviderName)
    : undefined;

  const [runProvider, setRunProvider] = useState<EnrichmentProviderName>('ares');
  const [runCap, setRunCap] = useState('');

  const runs = useEnrichmentRuns(true);
  const activeRun = runs.data?.find((r) => isRunActive(r.status));
  const stats = useEnrichmentStats(true, activeRun !== undefined);
  const matches = useEnrichmentMatches(status, providerFilter);

  const startRun = useMutation(trpc.enrichment.startRun.mutationOptions({ onSuccess: () => invalidateEnrichment() }));

  const items = useMemo(() => matches.data?.pages.flatMap((p) => p.items) ?? [], [matches.data]);
  const progress = progressOf(activeRun?.stats?.progress);
  // ARES runs end with a registry detail pass (NACE); once it reports, it IS the current phase
  const registryProgress = progressOf(activeRun?.stats?.registryProgress);
  const providerLabel = (provider: string) => t(`provider.${provider}`, { defaultValue: provider });

  function setStatus(value: EnrichmentMatchStatus) {
    const next = new URLSearchParams(searchParams);
    next.set('status', value);
    setSearchParams(next, { replace: true });
  }

  function setProviderFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('provider', value);
    else next.delete('provider');
    setSearchParams(next, { replace: true });
  }

  const providerConfigured = stats.data?.providersConfigured[runProvider] ?? runProvider === 'ares';

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      <StatsStrip stats={stats.data} />
      <ErrorAlert error={startRun.error ?? stats.error} />

      {activeRun && (
        <div className="bo-active-run" role="status">
          <Spinner>
            <strong>{t('activeRun.running', { provider: providerLabel(activeRun.provider) })}</strong>
          </Spinner>
          {registryProgress ? (
            <span className="bo-mono bo-muted">
              {t('activeRun.registry', {
                processed: registryProgress.processed ?? 0,
                of: registryProgress.of ?? '?',
              })}
            </span>
          ) : (
            progress && (
              <span className="bo-mono bo-muted">
                {t('activeRun.progress', { processed: progress.processed ?? 0, of: progress.of ?? '?' })}
              </span>
            )
          )}
        </div>
      )}

      {can('crm.run_jobs') && (
        <section className="hp-card">
          <HpCardHeader title={t('run.title')} subtitle={t('run.subtitle')} icon="badgeCheck" />
          <div className="hp-card__body">
            <div className="bo-inline-form">
              <HpField label={t('run.provider')} hint={canSpend ? undefined : t('run.providerHint')}>
                <select
                  value={runProvider}
                  onChange={(e) => setRunProvider(e.target.value as EnrichmentProviderName)}
                  aria-label={t('run.providerAria')}
                >
                  <option value="ares">{t('run.aresFree')}</option>
                  <option
                    value="apollo"
                    disabled={!canSpend || (stats.data ? !stats.data.providersConfigured.apollo : false)}
                  >
                    {stats.data && !stats.data.providersConfigured.apollo
                      ? t('run.noApiKey', { provider: t('provider.apollo') })
                      : t('provider.apollo')}
                  </option>
                  <option
                    value="lusha"
                    disabled={!canSpend || (stats.data ? !stats.data.providersConfigured.lusha : false)}
                  >
                    {stats.data && !stats.data.providersConfigured.lusha
                      ? t('run.noApiKey', { provider: t('provider.lusha') })
                      : t('provider.lusha')}
                  </option>
                </select>
              </HpField>
              {runProvider !== 'ares' && (
                <HpField label={t('run.cap')} hint={t('run.capHint')}>
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    placeholder="100"
                    value={runCap}
                    onChange={(e) => setRunCap(e.target.value)}
                  />
                </HpField>
              )}
              <div className="bo-inline-form__submit">
                <HpButton
                  variant="primary"
                  icon="play"
                  busy={startRun.isPending}
                  disabled={activeRun !== undefined || !providerConfigured}
                  onClick={() => {
                    const cap = Number(runCap);
                    startRun.mutate({
                      provider: runProvider,
                      ...(runProvider !== 'ares' && Number.isFinite(cap) && cap > 0 ? { cap } : {}),
                    });
                  }}
                >
                  {activeRun ? t('run.inProgress') : t('run.start')}
                </HpButton>
              </div>
            </div>
          </div>
        </section>
      )}

      <PublishSection canPublish={can('crm.publish')} pdDomain={me.data?.pipedriveCompanyDomain} />

      <PeopleFinder pdDomain={me.data?.pipedriveCompanyDomain} />

      <RunHistory lastRun={stats.data?.lastRun} />

      <section className="hp-card">
        <HpCardHeader title={t('matches.title')} subtitle={t('matches.subtitle')} icon="search">
          <FilterSelect
            value={providerFilter ?? ''}
            onChange={(e) => setProviderFilter(e.target.value)}
            aria-label={t('matches.filterByProvider')}
          >
            <option value="">{t('matches.allProviders')}</option>
            <option value="ares">{t('provider.ares')}</option>
            <option value="apollo">{t('provider.apollo')}</option>
            <option value="lusha">{t('provider.lusha')}</option>
          </FilterSelect>
        </HpCardHeader>
        <div className="bo-match-filters">
          <Segmented
            ariaLabel={t('matches.statusAria')}
            value={status}
            onChange={setStatus}
            options={TABS.map((tab) => ({
              value: tab.value,
              label: stats.data
                ? `${t(`tabs.${tab.value}`)} · ${fmtInt(tab.count(stats.data))}`
                : t(`tabs.${tab.value}`),
            }))}
          />
          <span className="bo-muted bo-mono">
            {matches.hasNextPage
              ? t('matches.loadedMore', { count: items.length })
              : t('matches.loaded', { count: items.length })}
          </span>
        </div>

        <ErrorAlert error={matches.error} className="bo-alert--inset" />

        {matches.isLoading && <p className="hp-card__body bo-muted">{t('matches.loading')}</p>}
        {!matches.isLoading && items.length === 0 && (
          <p className="hp-card__body bo-muted">{t('matches.none', { status: t(`tabs.${status}`) })}</p>
        )}
        {items.length > 0 && (
          <div className="bo-match-list">
            {items.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        )}
        {matches.hasNextPage && (
          <div className="hp-card__body bo-row">
            <LoadMore
              hasNextPage={matches.hasNextPage}
              isFetchingNextPage={matches.isFetchingNextPage}
              onClick={() => void matches.fetchNextPage()}
            />
          </div>
        )}
      </section>
    </div>
  );
}
